import type { PageContext } from '../../lib/types';
// Admin: Marketplace moderation — review listings before they go live, unlist bad ones, watch GMV and fees.
import { html, toast } from '../../lib/dom';
import { pageHead, table, kpi } from '../../components/ui';
import { coverThumb } from '../../components/thumb';
import { priceTag, stars, listingStatus } from '../../components/community';
import { db } from '../../api/core';
import { getCreator, marketplaceStats, setListingStatus, LISTING_STATUS_LABEL, PLATFORM_FEE } from '../../api/marketplace';
import { logAudit } from '../../api/admin';
import { money, num, relTime } from '../../lib/format';

const FILTERS = ['in_review', 'published', 'unlisted', 'draft', 'rejected', 'all'];

export function render(ctx: PageContext) {
  const status = FILTERS.includes(ctx.query.status) ? ctx.query.status : 'in_review';
  const rows = db.listings.filter((l?: any) => status === 'all' || l.status === status);
  const s = marketplaceStats();
  const count = (f?: any) => (f === 'all' ? db.listings.length : db.listings.filter((l?: any) => l.status === f).length);

  const body = html`<div class="page">
    ${pageHead({ eyebrow: 'Platform admin', title: 'Marketplace', lede: 'Every listing is reviewed before buyers can see it. Check the graph for leaked secrets or broken nodes, then approve or reject.' })}
    <div class="grid c4">
      ${kpi('GMV', money(s.gmv, 0), 'all-time, live + unlisted')}
      ${kpi('Platform fees', money(s.fees, 0), `${PLATFORM_FEE * 100}% of each sale`)}
      ${kpi('Waiting for review', s.inReview)}
      ${kpi('Live listings', s.live, `${s.sellers} sellers`)}
    </div>
    <div class="type-chips">${FILTERS.map((f?: any) => html`<button class="${status === f ? 'on' : ''}" data-param="status" data-value="${f}">${f === 'all' ? 'All' : LISTING_STATUS_LABEL[f]}<span class="n">${count(f)}</span></button>`)}</div>
    <div class="card">${table([
      { label: 'Listing', render: (l?: any) => html`<div class="cell-entity">${coverThumb(l)}<div class="col" style="gap:0"><strong style="font-weight:500">${l.title}</strong><span class="muted" style="font-size:12px">${l.category} · ${l.node_count} nodes · ${l.requirements.join(', ') || 'no providers'}</span></div></div>` },
      { label: 'Seller', render: (l?: any) => { const c = getCreator(l.seller_id); return html`<div class="col" style="gap:0"><span>${c.name}</span><span class="mono muted" style="font-size:11px">@${c.handle}${c.organization_id ? ' · Bots & Bones' : ' · independent'}</span></div>`; } },
      { label: 'Price', render: (l?: any) => priceTag(l.price) },
      { label: 'Sales', num: true, render: (l?: any) => num(l.sales_count) },
      { label: 'GMV', num: true, render: (l?: any) => money(l.price * l.sales_count, 0) },
      { label: 'Rating', render: (l?: any) => (l.rating ? html`${stars(l.rating)}` : html`<span class="muted">—</span>`) },
      { label: 'Status', render: listingStatus },
      { label: 'Updated', render: (l?: any) => relTime(l.updated_at) },
      { label: '', render: (l?: any) => html`<div class="row" style="justify-content:flex-end">${
        l.status === 'in_review' ? html`<button class="btn sm primary" data-mod="${l.id}:published">Approve</button><button class="btn sm ghost" data-mod="${l.id}:rejected">Reject</button>`
          : l.status === 'published' ? html`<button class="btn sm ghost" data-mod="${l.id}:unlisted">Unlist</button>`
            : l.status === 'unlisted' || l.status === 'rejected' ? html`<button class="btn sm" data-mod="${l.id}:published">Restore</button>` : ''}</div>` },
    ], rows, { rowHref: (l?: any) => `#/marketplace/${l.id}`, empty: 'Nothing here.' })}</div>
  </div>`;

  return {
    title: 'Admin · Marketplace', body,
    mount(root?: any) {
      root.querySelectorAll('[data-mod]').forEach((b?: any) => b.addEventListener('click', (e?: any) => {
        e.stopPropagation();
        const [id, to] = b.dataset.mod.split(':');
        setListingStatus(id, to);
        logAudit(`listing.${to}`, 'listing', id, { status: to });
        toast(`${db.listings.find((l?: any) => l.id === id).title} → ${LISTING_STATUS_LABEL[to]}`);
        ctx.rerender();
      }));
    },
  };
}
